<template>
  <div class="home-research-page">
    <!-- Hero Banner Section -->
    <HeroCarouselComp 
      :slides="researchSlides"
      :enable-particles="false"
      background-image="/banner/banner-three.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="researchMissionVision"
    />

    <!-- Research Gallery Section -->
    <ResearchItsGalleryComp :content="galleryContent" />

    <!-- Intro Section -->
    <ResearchItsIntroComp :content="introContent" />

    <!-- Technologies We Explore Section -->
    <ResearchItsTechComp :content="techContent" />

    <!-- Innovation Projects Section -->
    <ResearchItsInnovationComp :content="innovationContent" />

    <!-- Call to Action Section -->
    <ResearchItsCtaComp :content="ctaContent" />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { WOW } from 'wowjs'
import HeroCarouselComp from '@/components/HeroCarouselComp.vue'
import ResearchItsGalleryComp from '@/components/ResearchItsGalleryComp.vue'
import ResearchItsIntroComp from '@/components/ResearchItsIntroComp.vue'
import ResearchItsTechComp from '@/components/ResearchItsTechComp.vue'
import ResearchItsInnovationComp from '@/components/ResearchItsInnovationComp.vue'
import ResearchItsCtaComp from '@/components/ResearchItsCtaComp.vue'

export default {
  name: 'HomeResearchPage',
  components: {
    HeroCarouselComp,
    ResearchItsGalleryComp,
    ResearchItsIntroComp,
    ResearchItsTechComp,
    ResearchItsInnovationComp,
    ResearchItsCtaComp
  },
  setup() {
    const companiesSwiper = ref(null);
    const servicesSwiper = ref(null);
    const testimonialsSwiper = ref(null);
    const isMonthly = ref(true);

    // Gallery content
    const galleryContent = ref({
      header: {
        tag: "RESEARCH GALLERY",
        title: "Innovation in Action",
        subtitle: "Explore our cutting-edge research projects and breakthrough discoveries"
      },
      galleryItems: [
        {
          image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=800&fit=crop&crop=center',
          title: 'AI Research Lab',
          description: 'Advanced artificial intelligence research and machine learning algorithms development.',
          tags: ['AI', 'Machine Learning', 'Research'],
          size: 'tall'
        },
        {
          image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&h=600&fit=crop&crop=center',
          title: 'Blockchain Innovation',
          description: 'Exploring decentralized technologies and blockchain solutions.',
          tags: ['Blockchain', 'Crypto', 'Innovation'],
          size: 'wide'
        },
        {
          image: 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=600&h=600&fit=crop&crop=center',
          title: 'IoT Development',
          description: 'Internet of Things research and smart device development.',
          tags: ['IoT', 'Smart Devices', 'Connectivity'],
          size: 'square'
        },
        {
          image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=900&fit=crop&crop=center',
          title: 'Cloud Architecture',
          description: 'Scalable cloud solutions and distributed computing research.',
          tags: ['Cloud', 'Architecture', 'Scalability'],
          size: 'tall'
        },
        {
          image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop&crop=center',
          title: 'Data Analytics',
          description: 'Big data processing and advanced analytics research.',
          tags: ['Data Science', 'Analytics', 'Big Data'],
          size: 'wide'
        },
        {
          image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=600&fit=crop&crop=center',
          title: 'Cybersecurity Research',
          description: 'Advanced security protocols and threat detection systems.',
          tags: ['Security', 'Cybersecurity', 'Protection'],
          size: 'square'
        },
        {
          image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=600&h=800&fit=crop&crop=center',
          title: 'Quantum Computing',
          description: 'Next-generation quantum computing research and development.',
          tags: ['Quantum', 'Computing', 'Future Tech'],
          size: 'tall'
        },
        {
          image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&h=600&fit=crop&crop=center',
          title: 'Robotics & Automation',
          description: 'Intelligent robotics and automated systems research.',
          tags: ['Robotics', 'Automation', 'AI'],
          size: 'wide'
        }
      ]
    })

    // Intro content
    const introContent = ref({
      header: {
        label: "Our Research Focus",
        title: "10 Research Categories",
        description: "At the forefront of innovation, our research initiatives span across diverse domains to address real-world challenges, explore future technologies, and develop data-driven insights. Our interdisciplinary teams work at the intersection of technology, science, and business to drive meaningful impact."
      }
    })

    // Tech content
    const techContent = ref({
      header: {
        tag: "OUR FOCUS AREAS",
        title: "Our Research Categories",
        subtitle: "Our interdisciplinary teams work at the intersection of technology, science, and business to drive meaningful impact across diverse research domains."
      },
      technologies: [
        {
          image: '/resource/portfolio-01.jpg',
          icon: "fas fa-brain",
          category: "AI & Deep Learning",
          title: "Artificial Intelligence & Machine Learning",
          description: "Exploring advanced machine learning models, generative AI, neural networks, and autonomous systems to push the boundaries of intelligent computing.",
          features: ["Generative AI", "Neural Networks", "Autonomous Systems", "Intelligent Computing"],
          progress: 85
        },
        {
          image: '/resource/portfolio-02.jpg',
          icon: "fas fa-heartbeat",
          category: "Healthcare & Medical Innovation",
          title: "Healthcare & Medical Innovation",
          description: "Developing AI-powered diagnostic tools, digital health platforms, and data-driven solutions to transform patient care, medical research, and public health outcomes.",
          features: ["AI Diagnostics", "Digital Health", "Medical Research", "Public Health"],
          progress: 70
        },
        {
          image: '/resource/portfolio-03.jpg',
          icon: "fas fa-graduation-cap",
          category: "EdTech & Learning Sciences",
          title: "EdTech & Learning Sciences",
          description: "Researching technology-enhanced learning, adaptive education systems, and digital pedagogy to revolutionize how knowledge is delivered and consumed.",
          features: ["Adaptive Learning", "Digital Pedagogy", "Education Systems", "Knowledge Delivery"],
          progress: 90
        },
        {
          image: '/resource/portfolio-04.jpg',
          icon: "fas fa-link",
          category: "Blockchain & Web3 Technologies",
          title: "Blockchain & Web3 Technologies",
          description: "Investigating decentralized systems, smart contracts, and token economies to unlock transparency, security, and innovation in digital infrastructure.",
          features: ["Smart Contracts", "Token Economies", "Decentralized Systems", "Digital Infrastructure"],
          progress: 95
        }
      ]
    })

    // Innovation content
    const innovationContent = ref({
      header: {
        tag: "INNOVATION SHOWCASE",
        title: "Research Highlights & Case Studies",
        subtitle: "A curated library of applied research, success stories, and pilot studies across industries — demonstrating the real-world impact of our innovations."
      },
      projects: [
        {
          image: '/resource/about-us.png',
          icon: "fas fa-leaf",
          category: "Environmental & Sustainability Studies",
          title: "Climate Change & Resource Management",
          description: "Using data science, remote sensing, and predictive models to address climate change, resource management, and sustainable development goals (SDGs).",
          stats: [
            { value: "15+", label: "SDG Targets" },
            { value: "85%", label: "Accuracy Rate" }
          ],
          technologies: ["Data Science", "Remote Sensing", "Predictive Models"]
        },
        {
          image: '/resource/choose-01.png',
          icon: "fas fa-chart-line",
          category: "Market Intelligence & Forecasting",
          title: "Economic Modeling & Trend Analysis",
          description: "Applying econometrics, AI modeling, and trend analysis to predict market behavior, consumer patterns, and financial outcomes with strategic foresight.",
          stats: [
            { value: "92%", label: "Prediction Accuracy" },
            { value: "50+", label: "Markets Analyzed" }
          ],
          technologies: ["Econometrics", "AI Modeling", "Trend Analysis"]
        },
        {
          image: '/resource/choose-03.png',
          icon: "fas fa-users",
          category: "Behavioral & Social Analytics",
          title: "Human Behavior & Digital Interactions",
          description: "Understanding human behavior, societal trends, and digital interactions through social data, sentiment analysis, and psychological modeling.",
          stats: [
            { value: "1M+", label: "Data Points" },
            { value: "88%", label: "Accuracy Rate" }
          ],
          technologies: ["Sentiment Analysis", "Social Data", "Psychological Modeling"]
        },
        {
          image: '/resource/choose-04.png',
          icon: "fas fa-coins",
          category: "Fintech & Financial Modelling",
          title: "Next-Generation Financial Systems",
          description: "Building next-generation financial models, risk engines, fraud detection systems, and decentralized finance (DeFi) solutions to drive financial innovation.",
          stats: [
            { value: "99.9%", label: "Security Rate" },
            { value: "75%", label: "Risk Reduction" }
          ],
          technologies: ["Risk Engines", "Fraud Detection", "DeFi Solutions"]
        },
        {
          image: '/resource/portfolio-bg.jpg',
          icon: "fas fa-gavel",
          category: "LegalTech & Regulatory Compliance",
          title: "AI-Driven Legal Research",
          description: "Exploring automation in legal processes, compliance management systems, and AI-driven legal research to improve accessibility and efficiency in legal services.",
          stats: [
            { value: "90%", label: "Efficiency Gain" },
            { value: "24/7", label: "Availability" }
          ],
          technologies: ["Legal Automation", "Compliance Systems", "AI Research"]
        },
        {
          image: '/resource/portfolio-01.jpg',
          icon: "fas fa-book",
          category: "Case Study & Knowledge Repository",
          title: "Applied Research & Success Stories",
          description: "A curated library of applied research, success stories, and pilot studies across industries — demonstrating the real-world impact of our innovations.",
          stats: [
            { value: "100+", label: "Case Studies" },
            { value: "15+", label: "Industries" }
          ],
          technologies: ["Applied Research", "Success Stories", "Pilot Studies"]
        }
      ]
    })

    // CTA content
    const ctaContent = ref({
      backgroundImage: '/resource/portfolio-bg.jpg',
      header: {
        tag: "JOIN OUR JOURNEY",
        title: "Join Our Research Journey",
        description: "Be part of the future of technology. Explore our research opportunities, collaborate on innovative projects, and help shape tomorrow's solutions."
      },
      actions: [
        {
          icon: "fas fa-users",
          title: "Collaborate with Us",
          description: "Partner with our research team on cutting-edge projects and innovative solutions.",
          buttonText: "Start Collaboration",
          buttonType: "primary-btn",
          link: "/home-contact"
        },
        {
          icon: "fas fa-user-plus",
          title: "Join Our Team",
          description: "Become part of our innovative research team and help shape the future of technology.",
          buttonText: "View Careers",
          buttonType: "secondary-btn",
          link: "/careers"
        }
      ]
    })

    // Research slides data
    const researchSlides = ref([
      {
        backgroundImage: '/resource/portfolio-01.jpg',
        subtitle: 'Research Excellence',
        titleLines: [
          { text: 'Exploring Innovation ', highlight: false },
          { text: 'Through ', highlight: 'Research' }
        ],
        description: "Sogdo's R&D works across sectors — from AI to healthcare, from market research to case studies.",
        buttonText: 'Explore Research',
        buttonLink: '/home-contact'
      },
      {
        backgroundImage: '/resource/portfolio-02.jpg',
        subtitle: 'AI & Deep Learning',
        titleLines: [
          { text: 'Advanced ', highlight: 'Research' }
        ],
        description: 'Exploring advanced machine learning models, generative AI, neural networks, and autonomous systems.',
        buttonText: 'Learn More',
        buttonLink: '/home-contact'
      },
      {
        backgroundImage: '/resource/portfolio-03.jpg',
        subtitle: 'Healthcare Innovation',
        titleLines: [
          { text: 'Medical ', highlight: 'Research' }
        ],
        description: 'Developing AI-powered diagnostic tools, digital health platforms, and data-driven solutions.',
        buttonText: 'Get Started',
        buttonLink: '/home-contact'
      },
      {
        backgroundImage: '/resource/portfolio-04.jpg',
        subtitle: 'Blockchain & Web3',
        titleLines: [
          { text: 'Decentralized ', highlight: 'Systems' }
        ],
        description: 'Investigating decentralized systems, smart contracts, and token economies to unlock innovation.',
        buttonText: 'Explore Web3',
        buttonLink: '/home-contact'
      },
      {
        backgroundImage: '/resource/service-details.jpg',
        subtitle: 'Environmental Research',
        titleLines: [
          { text: 'Sustainability ', highlight: 'Studies' }
        ],
        description: 'Using data science, remote sensing, and predictive models to address climate change and sustainability.',
        buttonText: 'Join Research',
        buttonLink: '/home-contact'
      }
    ]);

    // Research mission/vision data
    const researchMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the most trusted software innovation partner, building meaningful technology for real-world challenges.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To build meaningful technology for real-world challenges through cutting-edge research, innovative development, and breakthrough discoveries that transform industries.'
      }
    });

    onMounted(async () => {
      // Initialize WOW.js
      new WOW({
        mobile: false,
        live: false
      }).init();
    });

    return {
      companiesSwiper,
      servicesSwiper,
      testimonialsSwiper,
      isMonthly,
      galleryContent,
      introContent,
      techContent,
      innovationContent,
      ctaContent,
      researchSlides,
      researchMissionVision
    };
  }
}
</script>

<style scoped>
/* Home Research page specific styles */
.home-research-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

@media (max-width: 768px) {
  .home-research-page section.banner__two {
    padding-top: 80px !important;
    padding-bottom: 80px !important;
  }
}

@media (max-width: 480px) {
  .home-research-page section.banner__two {
    padding-top: 60px !important;
    padding-bottom: 60px !important;
  }
}
</style>
